import {IconButton} from '@wandb/components';
import React, {useCallback} from 'react';

import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {panelViews} from '../PanelBank/types';
import {useGetIsMediaPanelSettingsDrawerSupportedForPanel} from '../PanelMediaBrowser/useGetIsMediaPanelSettingsDrawerSupportedForPanel';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {FullScreenPanelLink} from './FullScreenPanel/FullScreenPanelLink';
import {useIsPanelEditButtonVisible} from './hooks/useIsPanelEditButtonVisible';
import {StyledButtonWrapper} from './PanelActions.style';
import {usePanelContext} from './PanelContextProvider';
import {PanelPopupMode} from './types';

/**
 * This is soon to be deprecated.
 */
export const PanelEditButton = () => {
  const {panel} = usePanelContext();

  // Media panels have their own flows
  if (panel.viewType === panelViews.mediaBrowser) {
    return <PanelBankMediaPanelSettingsButton />;
  }

  // For the rest of panel types, we use the panel editor modal since
  // we haven not devoted time to migrating them into a drawer
  return <EditPanelWithModalButton buttonSize="sm" />;
};

export const EditPanelWithModalButton = ({
  buttonSize,
}: {
  buttonSize: 'sm' | 'md';
}) => {
  const {setPanelPopupMode, trackPanelControls} = usePanelContext();

  const onClick = useCallback(() => {
    setPanelPopupMode(PanelPopupMode.EDIT);
    trackPanelControls({action: 'Edit panel clicked'});
  }, [setPanelPopupMode, trackPanelControls]);

  return <EditButton buttonSize={buttonSize} onClick={onClick} />;
};

const EditButton = ({
  buttonSize,
  onClick,
}: {
  buttonSize: 'sm' | 'md';
  onClick: () => void;
}) => {
  return (
    <StyledButtonWrapper data-test="edit-panel-button">
      <IconButton
        icon="settings"
        variant="ghost"
        aria-label="Edit panel"
        size={buttonSize}
        tooltip={{children: 'Edit panel'}}
        onPress={e => {
          e.continuePropagation();
          onClick();
        }}
      />
    </StyledButtonWrapper>
  );
};

export const FullScreenPanelEditButton = () => {
  const {entityName} = useProjectParams();
  const {panel} = usePanelContext();
  const isPanelEditButtonVisible = useIsPanelEditButtonVisible(
    entityName,
    panel.viewType
  );

  if (!isPanelEditButtonVisible) {
    return null;
  }

  // Media panels have their own flows
  if (panel.viewType === panelViews.mediaBrowser) {
    return <FullScreenMediaPanelSettingsButton />;
  }

  // For the rest of panel types, we use the panel editor modal since
  // we haven not devoted time to migrating them into a drawer
  return <EditPanelWithModalButton buttonSize="md" />;
};

const FullScreenMediaPanelSettingsButton = () => {
  const {panel, panelRef, panelBankSectionConfigRef, trackPanelControls} =
    usePanelContext();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const getIsMediaPanelSettingsDrawerSupportedForPanel =
    useGetIsMediaPanelSettingsDrawerSupportedForPanel(panel);

  const onClick = useCallback(() => {
    window.scrollTo(0, 0);

    openWorkspaceDrawer?.(
      'media-settings',
      panelBankSectionConfigRef,
      panelRef
    );
    trackPanelControls({action: 'Edit panel clicked'});
  }, [
    openWorkspaceDrawer,
    panelBankSectionConfigRef,
    panelRef,
    trackPanelControls,
  ]);

  if (getIsMediaPanelSettingsDrawerSupportedForPanel()) {
    // We're already in full screen mode, so we can just open the drawer
    // Notice that the button is a different size in full screen mode
    return <EditButton buttonSize="md" onClick={onClick} />;
  }

  return <EditPanelWithModalButton buttonSize="md" />;
};

const PanelBankMediaPanelSettingsButton = () => {
  const {panel, panelRef, panelBankSectionConfigRef, trackPanelControls} =
    usePanelContext();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const getIsMediaPanelSettingsDrawerSupportedForPanel =
    useGetIsMediaPanelSettingsDrawerSupportedForPanel(panel);

  const onClick = useCallback(() => {
    window.scrollTo(0, 0);

    openWorkspaceDrawer?.(
      'media-settings',
      panelBankSectionConfigRef,
      panelRef
    );
    trackPanelControls({action: 'Edit panel clicked'});
  }, [
    openWorkspaceDrawer,
    panelBankSectionConfigRef,
    panelRef,
    trackPanelControls,
  ]);

  // When the media panel type is supported and we're just on a workspace, we want to
  // 1. Go into full screen panel page
  // 2. Open the media settings drawer by default
  if (getIsMediaPanelSettingsDrawerSupportedForPanel()) {
    return (
      <FullScreenPanelLink
        panel={panel}
        panelBankSectionConfigRef={panelBankSectionConfigRef}>
        <EditButton buttonSize="sm" onClick={onClick} />
      </FullScreenPanelLink>
    );
  }

  return <EditPanelWithModalButton buttonSize="sm" />;
};
